import { APPLICATION_ACCESS, TOAST_TYPE } from "../../constants";
import { CUSTOMER_AUTH_ROUTES } from "../../constants/customerApiRoutes";
import { getCookie, setLocalStorage, showToastMessage } from "../../utils";
import API from "../axios";
import { RecaptchaVerifier, signInWithPhoneNumber } from "firebase/auth";

export const customerSignUp = async (
  apiResponse: CallableFunction,
  handleLoading: CallableFunction,
  data: any
) => {
  try {
    const locationInfo = getCookie("coordinates");
    const response = await API.post(CUSTOMER_AUTH_ROUTES.SIGNUP, {
      ...data,
      lat: data?.lat || locationInfo?.latitude || "",
      long: data?.long || locationInfo?.longitude || "",
    });
    if (response.status === 201) {
      showToastMessage(TOAST_TYPE.SUCCESS, response.data.message);
      apiResponse(response?.data?.data);
    } else {
      showToastMessage(TOAST_TYPE.ERROR, response.data.message);
    }
  } catch (error: any) {
    showToastMessage(
      TOAST_TYPE.ERROR,
      error?.response?.data?.message ?? error?.message
    );
  } finally {
    handleLoading(false);
  }
};

export const verifyAccount = async (
  data: any,
  handleLoading: CallableFunction,
  handleData: CallableFunction
) => {
  try {
    const response = await API.post(CUSTOMER_AUTH_ROUTES.VERIFY, data);

    const applicationAccess: any = APPLICATION_ACCESS;
    const userRole = response?.data?.data?.user?.user_role;

    const useApplicationRole = (() => {
      if (userRole === 5) {
        const sellerType = response?.data?.data?.user?.seller_type;
        return applicationAccess[5]?.[sellerType];
      } else {
        return applicationAccess[userRole];
      }
    })();

    if (userRole === 5) {
      setLocalStorage("sellerType", useApplicationRole?.access);
    }

    setLocalStorage("accessToken", response?.data?.data.token);
    setLocalStorage("userId", response?.data?.data?.user?._id);
    setLocalStorage("userRole", useApplicationRole?.type);
    handleData(response?.data?.data);
    showToastMessage(TOAST_TYPE.SUCCESS, response.data.message);
  } catch (error: any) {
    showToastMessage(
      TOAST_TYPE.ERROR,
      error?.response?.data?.message ?? error?.message
    );
  } finally {
    handleLoading(false);
  }
};

export const resendVerificationEmail = async (
  data: any,
  callback: CallableFunction,
  handleLoading: CallableFunction
) => {
  try {
    const response = await API.post(CUSTOMER_AUTH_ROUTES.REVERIFY, data);
    showToastMessage(TOAST_TYPE.SUCCESS, response.data.message);
    callback();
  } catch (error: any) {
    showToastMessage(
      TOAST_TYPE.ERROR,
      error?.response?.data?.message ?? error?.message
    );
  } finally {
    handleLoading(false);
  }
};

export const customerChangePassword = async (
  data: any,
  handleLoading: CallableFunction,
  callback: CallableFunction
) => {
  try {
    const response = await API.post(CUSTOMER_AUTH_ROUTES.CHANGE_PASSWORD, data);
    if (response.status === 200) {
      showToastMessage(TOAST_TYPE.SUCCESS, response.data.message);
      callback();
    } else {
      showToastMessage(TOAST_TYPE.ERROR, response.data.message);
    }
  } catch (error: any) {
    showToastMessage(
      TOAST_TYPE.ERROR,
      error?.response?.data?.message ?? error?.message
    );
  } finally {
    handleLoading(false);
  }
};

export const fpSendMail = async (
  apiResponse: CallableFunction,
  handleLoading: CallableFunction,
  data: any
) => {
  try {
    const response = await API.post(
      CUSTOMER_AUTH_ROUTES.CUSTOMER_FP_SENDMAIL,
      data
    );
    apiResponse(response);
    if (response.status === 200) {
      showToastMessage(TOAST_TYPE.SUCCESS, response.data.message);
    } else {
      showToastMessage(TOAST_TYPE.ERROR, response.data.message);
    }
  } catch (error: any) {
    showToastMessage(
      TOAST_TYPE.ERROR,
      error?.response?.data?.message ?? error?.message
    );
  } finally {
    handleLoading(false);
  }
};

// Set up reCAPTCHA
export const setupRecaptcha = (auth: any) => {
  if (!(window as Window | any).recaptchaVerifier) {
    (window as Window | any).recaptchaVerifier = new RecaptchaVerifier(
      auth,
      "recaptcha-container",
      {
        size: "invisible",
        callback: () => {
          console.log("reCAPTCHA verified");
        },
        "expired-callback": function () {
          console.log("reCAPTCHA expired");
        },
        defaultCountry: "IN",
      }
    );
  }
};

// Handle sending OTP
export const sendOtp = (
  appVerifier: any,
  auth: any,
  phoneNumber: string,
  handleCallback: CallableFunction
) => {
  signInWithPhoneNumber(auth, phoneNumber, appVerifier)
    .then((confirmation) => {
      handleCallback(confirmation);
      showToastMessage(TOAST_TYPE.SUCCESS, `OTP sent on ${phoneNumber}`);
    })
    .catch((error) => {
      console.error("Error sending OTP:", error?.code);
      if (error?.code === "auth/too-many-requests") {
        showToastMessage(
          TOAST_TYPE.ERROR,
          "Too many requests. Please try again after 24 hours."
        );
      } else if (error.error)
        showToastMessage(
          TOAST_TYPE.ERROR,
          `Error sending OTP ${error?.message?.toString()}`
        );

      handleCallback();
    });
};

// Handle OTP verification
export const verifyOtp = (
  confirmationResult: any,
  otp: string,
  handleLoading: CallableFunction,
  handleCallback: CallableFunction
) => {
  if (confirmationResult) {
    confirmationResult
      .confirm(otp)
      .then((result: any) => {
        const user = result.user;
        const data = { contact_number: user?.phoneNumber?.slice(3) };
        verifyAccount(data, handleLoading, handleCallback);
      })
      .catch((error: any) => {
        console.error("Error verifying OTP:", error);
        showToastMessage(
          TOAST_TYPE.ERROR,
          `Error verifying OTP ${error?.message?.toString()}`
        );
        handleLoading(false);
      });
  } else {
    showToastMessage(TOAST_TYPE.ERROR, "Please request an OTP first.");
  }
};
